import { useEffect, useMemo } from 'react';
import { cloneDeep, find, findIndex } from 'lodash';

import { useSpecialsContext } from '@/context';
import { useProducts } from '@/hooks';
import { CartLineItem, ISpecial, ISpecialProduct } from '@/types';

import { computeBundleSpecialProgress } from './bundleProgress';
import { useSpecialGroupData } from './useSpecialGroupData';

interface IUseBundleSpecial {
  id?: string;
}

interface IUseBunbleSpecialResponse {
  bundleProducts: CartLineItem[];
  currentBundleProducts: CartLineItem[];
  dependentProducts: CartLineItem[];
  independentProducts: CartLineItem[];
  initDepProducts: ISpecialProduct[];
  initIndProducts: ISpecialProduct[];
  isLoading: boolean;
  processingDependents: boolean;
  processingIndependents: boolean;
  special?: ISpecial;
}

const EMPTY_BUNDLE_PROGRESS = {
  bundleProducts: [],
  currentBundleProducts: [],
  dependentProducts: [],
  independentProducts: [],
  initDepProducts: undefined,
  initIndProducts: undefined,
  processingDependents: false,
  processingIndependents: false,
  special: undefined,
};

const useBundleSpecial = ({
  id,
}: IUseBundleSpecial): IUseBunbleSpecialResponse => {
  const { products, update } = useProducts();
  const { isLoading, specials, activesPreCartDiscounts } = useSpecialsContext();

  const accounting = products?.reduce((result, current) => {
    return result + current?.quantity;
  }, 0);

  const currentSpecial = specials?.find(sp => sp?.id === id);

  const cmsDiscountItem = currentSpecial?.id
    ? activesPreCartDiscounts?.[currentSpecial.id]
    : undefined;

  const { groupData, isLoading: isBogoLoading } =
    useSpecialGroupData(currentSpecial);

  const {
    bundleProducts,
    currentBundleProducts,
    dependentProducts,
    independentProducts,
    initDepProducts,
    initIndProducts,
    processingDependents,
    processingIndependents,
    special,
  } = useMemo(() => {
    if (isLoading || !currentSpecial?.id) {
      return EMPTY_BUNDLE_PROGRESS;
    }

    const groupIds = [
      ...(currentSpecial.groupsRequired ?? []),
      ...(currentSpecial.groups ?? []),
    ];

    if (groupIds.length > 0 && isBogoLoading) {
      return EMPTY_BUNDLE_PROGRESS;
    }

    return (
      computeBundleSpecialProgress(
        products ?? [],
        currentSpecial,
        groupData,
        isBogoLoading,
        cmsDiscountItem,
      ) ?? EMPTY_BUNDLE_PROGRESS
    );
  }, [
    accounting,
    products,
    currentSpecial,
    cmsDiscountItem,
    groupData,
    isBogoLoading,
    isLoading,
  ]);

  const cartLinesForFlagSync =
    products?.filter(
      line => line.bundle_id && line.bundle_id === currentSpecial?.id,
    ) ?? [];

  useEffect(() => {
    if (!cartLinesForFlagSync.length || !special) return;

    // BOGO progress is computed live from the route-eligible cart lines, so BOGO
    // lines don't need persisted independent/dependent flags. Only non-BOGO bundles
    // read those flags (the reduce branch in classifyBundleUnits), so the sync below
    // runs for them alone.
    if (special.method === 'BOGO' || !bundleProducts.length) return;

    let hasFlagChanges = false;

    cartLinesForFlagSync.forEach((line: CartLineItem) => {
      const index = findIndex(
        products,
        (p: CartLineItem) => p._id === line._id,
      );

      if (index === -1) return;

      const syncedLine =
        find(bundleProducts, p => p._id === line._id) ?? products[index];

      if (
        syncedLine.independent !== products[index].independent ||
        syncedLine.dependent !== products[index].dependent
      ) {
        hasFlagChanges = true;
      }
    });

    if (!hasFlagChanges) return;

    const newProducts = cloneDeep(products);

    cartLinesForFlagSync.forEach((line: CartLineItem) => {
      const index = findIndex(
        products,
        (p: CartLineItem) => p._id === line._id,
      );

      if (index === -1) return;

      newProducts[index] =
        find(bundleProducts, p => p._id === line._id) ?? newProducts[index];
    });

    update(newProducts);
  }, [bundleProducts]);

  return {
    bundleProducts,
    currentBundleProducts,
    dependentProducts,
    independentProducts,
    initDepProducts: initDepProducts!,
    initIndProducts: initIndProducts!,
    isLoading: isLoading || isBogoLoading,
    processingDependents,
    processingIndependents,
    special,
  };
};

export default useBundleSpecial;
